"use client";

import React, { useState, useEffect, useRef } from "react";
import { ChevronRight } from "lucide-react";

export type ExplainableColor = "purple" | "emerald" | "amber" | "sky" | "rose";

export interface ExplainableInlineProps {
  id?: string;
  term: string;
  explanation: string;
  category?: string;
  color?: ExplainableColor;
  mode?: "typewriter" | "instant";
  speedMs?: number;
  className?: string;
}

const CATEGORY_TO_COLOR: Record<string, ExplainableColor> = {
  "KI & Agents": "purple",
  "Web Standards & a11y": "emerald",
  "Performance & Architektur": "amber",
  "Workflows & Testing": "sky",
  "UI & Design Systems": "rose",
};

const COLOR_STYLES: Record<
  ExplainableColor,
  {
    textClosed: string;
    textOpen: string;
    chevronClosed: string;
    chevronOpen: string;
    focusRing: string;
  }
> = {
  purple: {
    textClosed: "text-neutral-900 dark:text-neutral-100 hover:text-purple-600 dark:hover:text-purple-400",
    textOpen: "text-purple-700 dark:text-purple-300",
    chevronClosed: "text-purple-500 group-hover:text-purple-600",
    chevronOpen: "text-purple-600 dark:text-purple-400",
    focusRing: "focus-visible:ring-purple-500",
  },
  emerald: {
    textClosed: "text-neutral-900 dark:text-neutral-100 hover:text-emerald-600 dark:hover:text-emerald-400",
    textOpen: "text-emerald-700 dark:text-emerald-300",
    chevronClosed: "text-emerald-500 group-hover:text-emerald-600",
    chevronOpen: "text-emerald-600 dark:text-emerald-400",
    focusRing: "focus-visible:ring-emerald-500",
  },
  amber: {
    textClosed: "text-neutral-900 dark:text-neutral-100 hover:text-amber-600 dark:hover:text-amber-400",
    textOpen: "text-amber-700 dark:text-amber-300",
    chevronClosed: "text-amber-500 group-hover:text-amber-600",
    chevronOpen: "text-amber-600 dark:text-amber-400",
    focusRing: "focus-visible:ring-amber-500",
  },
  sky: {
    textClosed: "text-neutral-900 dark:text-neutral-100 hover:text-sky-600 dark:hover:text-sky-400",
    textOpen: "text-sky-700 dark:text-sky-300",
    chevronClosed: "text-sky-500 group-hover:text-sky-600",
    chevronOpen: "text-sky-600 dark:text-sky-400",
    focusRing: "focus-visible:ring-sky-500",
  },
  rose: {
    textClosed: "text-neutral-900 dark:text-neutral-100 hover:text-rose-600 dark:hover:text-rose-400",
    textOpen: "text-rose-700 dark:text-rose-300",
    chevronClosed: "text-rose-500 group-hover:text-rose-600",
    chevronOpen: "text-rose-600 dark:text-rose-400",
    focusRing: "focus-visible:ring-rose-500",
  },
};

export function ExplainableInline({
  id,
  term,
  explanation,
  category,
  color,
  mode = "typewriter",
  speedMs = 12,
  className = "",
}: ExplainableInlineProps) {
  // Determine color theme based on category or explicit prop
  const effectiveColor: ExplainableColor =
    color || (category && CATEGORY_TO_COLOR[category]) || "purple";

  const theme = COLOR_STYLES[effectiveColor] || COLOR_STYLES.purple;

  const componentId =
    id ||
    `exp-${term
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 30)}`;

  const [isExpanded, setIsExpanded] = useState(false);
  const [displayedLength, setDisplayedLength] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean explanation text
  const cleanExplanation = explanation.trim();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isExpanded) {
      setDisplayedLength(0);
      return;
    }

    if (mode === "instant") {
      setDisplayedLength(cleanExplanation.length);
      return;
    }

    // Accelerating typewriter easing
    setDisplayedLength(0);
    let currentLength = 0;

    const scheduleNext = () => {
      currentLength += 1;
      setDisplayedLength(currentLength);

      if (currentLength < cleanExplanation.length) {
        const progress = currentLength / cleanExplanation.length;
        // Starts gently at ~28ms and accelerates to ~4ms
        const delay = Math.max(4, Math.round(speedMs * (1.8 - Math.pow(progress, 0.6) * 1.4)));
        timeoutRef.current = setTimeout(scheduleNext, delay);
      } else {
        timeoutRef.current = null;
      }
    };

    scheduleNext();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isExpanded, cleanExplanation, mode, speedMs]);

  const toggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const isComplete = displayedLength >= cleanExplanation.length;

  return (
    <span className={`inline ${className}`}>
      {/* Trigger Button: Clean, bold text without clashing underline */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isExpanded}
        aria-controls={componentId}
        aria-label={`${term}: ${isExpanded ? "Erklärung einklappen" : "Erklärung im Lesefluss entfalten"}`}
        className={`group relative inline-flex items-center gap-0.5 align-baseline cursor-pointer font-bold transition-colors duration-150 outline-none select-none rounded-sm px-0.5 mx-0.5 focus-visible:ring-2 ${theme.focusRing} ${
          isExpanded ? theme.textOpen : theme.textClosed
        }`}
      >
        <span>{term}</span>
        <ChevronRight
          className={`h-3 w-3 shrink-0 transition-transform duration-200 ${
            isExpanded
              ? `rotate-180 ${theme.chevronOpen}`
              : `${theme.chevronClosed} group-hover:translate-x-0.5`
          }`}
          aria-hidden="true"
        />
      </button>

      {/* SEO & DOM Crawler Anchor (pre-rendered in static HTML for LLMs/Google) */}
      {!isExpanded && (
        <span
          id={componentId}
          className="hidden"
          aria-hidden="true"
          data-seo-content="true"
        >
          {` (${cleanExplanation})`}
        </span>
      )}

      {/* Expanded Inline Text: Natural text flow, no background box, natural parenthesis spacing */}
      {isExpanded && (
        <span
          id={componentId}
          className="inline font-normal text-neutral-700 dark:text-neutral-300"
        >
          {/* Explicit non-breaking spacing before opening parenthesis */}
          <span className="text-neutral-400 dark:text-neutral-500 font-medium select-none">
            {" ("}
          </span>
          <span className="inline">
            {mode === "typewriter" && !isComplete
              ? cleanExplanation.slice(0, displayedLength)
              : cleanExplanation}
          </span>
          <span className="text-neutral-400 dark:text-neutral-500 font-medium select-none">
            {")"}
          </span>
        </span>
      )}
    </span>
  );
}
